import React, { FC, useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import classNames from 'classnames';

import { Modal } from '@shared/components/Modal';
import { useExecutionsUIStore } from '@core/useStores';

import { useStyles } from './StepModal.styles';
import { ThemeProps } from '@styles/theme';

import CloseIcon from '@assets/close-icon.svg'
import TimeIcon from '@assets/time-icon.svg';
import ErrorIcon from '@assets/error-icon.svg';
import AudioIcon from '@assets/audio-icon.svg';
import VideoIcon from '@assets/video.svg';

import { getDurationSeconds, VideoPlayer } from '@shared/components/VideoPlayer';
import { formatEventTypeLabel, getStepCompletionStatus, getStepDesignedNumber, getStepErrorCount, getStepEvents, getStepMediaItems, hasDisplayableEventValue, isErrorEventType, isMandatoryStep } from 'NeoAnalytics/executionSteps';
import { getExecutionStepSelectionKey } from 'NeoAnalytics/stores/ExecutionsUI.store';
import { useResolvedMediaUrl } from 'NeoAnalytics/hooks/useResolvedMediaUrl';

export type StepModalProps = {
    isOpen: boolean,
    onRequestClose: () => void,
}

export function getStartTime(parsedA: number, parsedB: number): string {
    const diffMs = Math.abs(parsedA - parsedB);

    const totalSeconds = Math.floor(diffMs / 1000);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const centiseconds = Math.floor((diffMs % 1000) / 10); // 0..99

    const hh = hours > 0 ? `${String(hours).padStart(2, "0")}:`: '';
    const mm = `${String(minutes).padStart(2, "0")}:`;
    const ss = String(seconds).padStart(2, "0");
    const cs = (centiseconds > 0 && hours == 0) ? `.${String(centiseconds).padStart(2, "0")}` : '';

    return hh + mm + ss + cs;
}

export function getDurationString(ts: string): string {
    const m = (ts ?? "").trim().match(/^(\d+):([0-5]?\d):([0-5]?\d)(?:\.(\d+))?$/);
    if (!m) {return "00:00:00";}

    const h = Number(m[1]) || 0;
    const min = Number(m[2]) || 0;
    const sec = Number(m[3]) || 0;

    const pad2 = (n: number) => n.toString().padStart(2, "0");
    if(min == 0){
        return `${pad2(min)}:${pad2(sec)} s`;
    }else{
        return `${pad2(h)}:${pad2(min)}:${pad2(sec)} s`;
    }
}

function toDisplayEntries(value: unknown): Array<{ key: string; value: string }> {
    if (value == null) {return [];}

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return [{ key: 'Value', value: String(value) }];
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
        return Object.entries(value as Record<string, unknown>)
            .filter(([key, raw]) => !key.startsWith('_') && raw != null)
            .map(([key, raw]) => ({
                key,
                value: typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean'
                    ? String(raw)
                    : JSON.stringify(raw),
            }));
    }

    try {
        return [{ key: 'Value', value: JSON.stringify(value) }];
    } catch {
        return [];
    }
}

function formatEntryLabel(value: string): string {
    return value
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .trim();
}

type SelectableExecutionStep = {
    id?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    name?: string | null;
    parentStepId?: string | null;
};

export function getSelectedExecutionStep<T extends SelectableExecutionStep>(
    steps: readonly T[] | null | undefined,
    selectedStepId: string | null | undefined,
): T | undefined {
    if (!steps?.length || !selectedStepId) {
        return undefined;
    }

    return steps.find((step, index) => getExecutionStepSelectionKey(step, index) === selectedStepId)
        ?? steps.find(step => step.id === selectedStepId);
}

type ResolvedAudioProps = {
    url: string,
    className?: string,
};

const ResolvedAudio: FC<ResolvedAudioProps> = ({ url, className }) => {
    const resolvedUrl = useResolvedMediaUrl(url);

    if (!resolvedUrl) {
        return null;
    }

    return (
        <audio className={className} controls preload="none" src={resolvedUrl}>
            Your browser does not support audio playback.
        </audio>
    );
};

export const StepModal: FC<StepModalProps> = ({
    isOpen,
    onRequestClose,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const executionsUIStore = useExecutionsUIStore();
    const [expandedEventIds, setExpandedEventIds] = useState<string[]>([]);
    const resolvedVideoUrl = useResolvedMediaUrl(executionsUIStore.execution?.mediaUrl, isOpen);

    const toggleEventValue = (eventId: string) => {
        setExpandedEventIds(current => current.includes(eventId)
            ? current.filter(id => id !== eventId)
            : [...current, eventId]);
    };

    const getStepStatusLabel = (hasExecutionEnded: boolean, hasStepEnded: boolean, stepStatus: string) => {
        if (!hasStepEnded) {
            return hasExecutionEnded ? 'Not Completed' : 'In Progress';
        }

        return stepStatus;
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className={styles.modal}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc
            noPadding
        >
            <Observer>
                {() => {
                    const data = executionsUIStore.execution;

                    const selectedStep = getSelectedExecutionStep(data?.steps, executionsUIStore.selectedStepId);

                    if(selectedStep){
                        const stepStart = (Date.parse(selectedStep?.startTime ?? '') -  Date.parse(data?.startTime ?? ''))/1000;

                        const videoDuration = getDurationSeconds(selectedStep?.duration);

                        const events = getStepEvents(data, selectedStep);
                        const errorCount = getStepErrorCount(data, selectedStep);
                        const designedStepNumber = getStepDesignedNumber(selectedStep);
                        const completionStatus = getStepStatusLabel(Boolean(data?.endTime), Boolean(selectedStep?.endTime), formatEventTypeLabel(getStepCompletionStatus(selectedStep)));
                        const isMandatory = isMandatoryStep(selectedStep);
                        const mediaItems = getStepMediaItems(data, selectedStep);
                        const audioItems = mediaItems.filter(item => item.kind === 'audio' && (!item.stepId || item.stepId === selectedStep.id));
                        const hasVideo = Boolean(data?.hasMedia && data?.mediaUrl);

                        return(
                            <div className={styles.modalContainer}>
                                <div className={styles.modalHeader}>
                                    {selectedStep?.name}
                                    <div className={styles.imageContainer}>
                                        <img src={CloseIcon} onClick={onRequestClose}/>
                                    </div>
                                </div>
                                <div className={styles.modalBody}>
                                    <div className={styles.contentLeft}>
                                        {selectedStep?.description ?
                                            <p>
                                                {selectedStep.description}
                                            </p>
                                            :
                                            <p className={styles.placeholder}>
                                            no description available
                                            </p>}
                                        <div className={styles.stepMetaContainer}>
                                            {designedStepNumber &&
                                                <div className={styles.stepMetaChip}>
                                                    <span>Step {designedStepNumber}</span>
                                                </div>
                                            }
                                            <div className={styles.stepMetaChip}>
                                                <span>{completionStatus}</span>
                                            </div>
                                            {isMandatory &&
                                                <div className={styles.stepMetaChip}>
                                                    <span>Mandatory</span>
                                                </div>
                                            }
                                        </div>
                                        <div className={styles.stepDataHeader}>
                                            <div className={styles.timeContainer}>
                                                <img src={TimeIcon} className={styles.timeIcon}/>
                                                <p>
                                                    {getDurationString(selectedStep?.duration ?? '')}
                                                </p>
                                            </div>
                                            <div className={styles.averageTimeContainer}>
                                                <b>
                                                    Start Time
                                                </b>
                                                <p>
                                                    {getStartTime(Date.parse(selectedStep?.startTime ?? ''), Date.parse(data?.startTime ?? ''))}
                                                </p>
                                            </div>
                                            <div className={styles.errorsCounterContainer}>
                                                <img src={ErrorIcon} />
                                                <p>
                                                    {errorCount}
                                                </p>
                                            </div>
                                        </div>
                                        {audioItems.length > 0 &&
                                            <div className={styles.voiceRecordingContainer}>
                                                <div className={styles.voiceRecordingLabel}>
                                                    <img src={AudioIcon}/>
                                                    <p>
                                                        Voice recording
                                                    </p>
                                                </div>
                                                {audioItems.map(audioItem => (
                                                    <div key={audioItem.id} className={styles.voiceRecording}>
                                                        <div className={styles.timeContainer}>
                                                            <img src={TimeIcon} className={styles.timeIcon}/>
                                                            <p>
                                                                {audioItem.timeStamp ? getStartTime(Date.parse(audioItem.timeStamp), Date.parse(data?.startTime ?? '')) : '00:00'}
                                                            </p>
                                                        </div>
                                                        <ResolvedAudio className={styles.audioPlayer} url={audioItem.url}/>
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                        {hasVideo &&
                                            <div className={styles.videoContainer}>
                                                <div className={styles.videoHeader}>
                                                    <img src={VideoIcon}/>
                                                    <p>
                                                        Video recording
                                                    </p>
                                                </div>
                                                {resolvedVideoUrl &&
                                                    <VideoPlayer videoSrc={resolvedVideoUrl} videoStart={stepStart} duration={videoDuration}/>
                                                }
                                            </div>
                                        }
                                    </div>
                                    <div className={styles.contentRight}>
                                        <p>
                                            Details
                                        </p>
                                        <div className={styles.eventsWrapper}>
                                            {events && events?.map((e, eventIndex) => {
                                                const isErrorEvent = isErrorEventType(e.type);
                                                const eventDescription = (e as { description?: string | null }).description;
                                                const eventId = `${e.timestamp ?? 'event'}-${e.type ?? 'type'}-${eventIndex}`;
                                                const valueEntries = toDisplayEntries((e as { value?: unknown }).value);
                                                const canShowValue = hasDisplayableEventValue((e as { value?: unknown }).value) && valueEntries.length > 0;
                                                const isExpanded = expandedEventIds.includes(eventId);

                                                return(
                                                    <div key={eventId} className={classNames(styles.eventContainer, isErrorEvent && styles.errorEventContainer)}>
                                                        <div className={classNames(styles.eventHedaer, isErrorEvent && styles.errorEventHeader)}>
                                                            <b>
                                                                {formatEventTypeLabel(e.type)}
                                                            </b>
                                                            <p>
                                                                {getStartTime(Date.parse(e.timestamp!), Date.parse(data?.startTime ?? ''))}
                                                            </p>
                                                        </div>

                                                        <div className={styles.eventDescription}>
                                                            <p>
                                                                {eventDescription ?? 'No description available'}
                                                            </p>
                                                        </div>
                                                        {canShowValue &&
                                                            <div className={styles.eventActions}>
                                                                <button type="button" className={styles.detailsToggle} onClick={() => toggleEventValue(eventId)}>
                                                                    {isExpanded ? 'Hide details' : 'Show details'}
                                                                </button>
                                                            </div>
                                                        }
                                                        {canShowValue && isExpanded &&
                                                            <div className={styles.eventValuePanel}>
                                                                {valueEntries.map(entry => (
                                                                    <div key={`${eventId}-${entry.key}`} className={styles.eventValueCard}>
                                                                        <div className={styles.eventValueLabel}>{formatEntryLabel(entry.key)}</div>
                                                                        <div className={styles.eventValueText}>{entry.value}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        }
                                                    </div>
                                                )
                                            })}
                                            {events && events.length == 0 &&
                                                <div>
                                                    <p className={styles.placeholder}>
                                                        No Events in this Step
                                                    </p>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }else{
                        return(

                            <div className={styles.modalContainer}>
                                <div className={styles.modalHeader}>
                                    <p className={styles.placeholder}>
                                        Selected Step Not Found
                                    </p>
                                </div>
                            </div>
                        )
                    }
                }}
            </Observer>
        </Modal>
    )
}
